var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState } from 'react';
import { Controller, FormProvider, useForm, } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Button, Checkbox, LoadingButton, SelectionControlLabel, TextField, Typography, } from '@one-thd/sui-atomic-components';
import { TextToConfirm } from '@one-thd/sui-icons';
import { useConfigService } from '@thd-nucleus/experience-context';
import { FormTitle } from '../../shared/form-title/form-title';
import { validationSchema } from './validation';
import { getHyphenatedPhoneNumber } from '../../../utils/masking-field';
import { ProHouseAccountInfo } from './pro-house-account-info/pro-house-account-info';
import { InfoPopover } from '../../shared/popover/popover';
import { T2CTermsAndConditions } from '../remote-purchases-auth/t2c-terms-conditons';
import { onTooltipClick } from '../../../utils/remote-purchases';
import { TAlertStatus, } from '../../../models/common/alert-messages';
import { usePayments } from '../../../hooks/use-payments';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { getT2CSuccessMessage } from '../../../utils/pro-house-account';
import { usePhaUnenrollT2cConfirmModal } from '../../../hooks/use-pha-unenroll-t2c-confirm-modal';
import { TProHouseAccountAction, } from '../../../models/b2b/payments';
import { UnenrollT2CConfirmModalBodyText, UnenrollT2CConfirmModalHeaderText, } from '../../../constants/pro-house-account';
import { ERR_GENERIC, ERR_MAX_T2C_TENDER_LIMIT, } from '../../../constants/response-error-messages';
import { removeNonDigitsFromString, formatRedactedPhoneNumbers, extractPhoneNumbersFromErrorMessage, getDuplicateT2cErrorMessage, } from '../../../utils/general';
import { MAX_T2C_TENDER_LIMIT_REACHED_CODE, T2C_NUMBER_ASSOCIATED_ANOTHER_ACCOUNT, } from '../../../constants/error-codes';
import { getPaymentsErrorCode } from '../../../utils/errors';
import { MultipleApproversSection } from '../remote-purchases-auth/multiple-approvers-section';
import { ptcRemotePurchasesPtcPostPageErrorAnalytics } from '../../../utils/payments-analytics';
const getTextFieldStatus = (error) => error ? 'error' : undefined;
export const ProHouseAccountAuthForm = ({ onCloseForm, onSuccess, onFailure, card, }) => {
    var _a;
    const [formAlertMessage, setFormAlertMessage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { enrollPHAT2C, unenrollPHAT2C, t2cMultipleApproverContacts } = usePayments();
    const { svocID } = useCustomerInfo();
    const [openConfirmModal] = usePhaUnenrollT2cConfirmModal({
        selectedCard: card,
    });
    const { t2cNumber, paymentId } = card;
    const t2cStatus = (_a = card.t2cStatus) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    const t2cPending = t2cStatus === 'pending';
    const isT2CInitiallyChecked = t2cPending || t2cStatus === 'enrolled';
    const activeT2COnFile = card.activeT2COnFile;
    const defaultT2CNumber = t2cNumber || activeT2COnFile || '';
    const multipleApproversAllowedSvocIDs = useConfigService('T2C_MULTIPLE_APPROVERS_ALLOWED_SVOCIDS');
    const isSvocIDAllowedForMultipleAprovers = typeof multipleApproversAllowedSvocIDs === 'string' &&
        multipleApproversAllowedSvocIDs.includes(svocID);
    const t2cMultipleApproversEnabled = !!useConfigService('T2C_MULTIPLE_APPROVERS_ENABLED') ||
        isSvocIDAllowedForMultipleAprovers;
    const hasMultipleApproversOnFile = !!(t2cMultipleApproverContacts && t2cMultipleApproverContacts.length > 0);
    const defaultValues = {
        t2cCheckbox: isT2CInitiallyChecked,
        phoneNumber: getHyphenatedPhoneNumber(defaultT2CNumber),
        additionalApprovers: hasMultipleApproversOnFile
            ? t2cMultipleApproverContacts.map(({ fullName, t2cNumber: number, status }) => ({
                fullName: fullName || '',
                t2cNumber: getHyphenatedPhoneNumber(number),
                status,
            }))
            : [
                {
                    fullName: '',
                    t2cNumber: getHyphenatedPhoneNumber(defaultT2CNumber),
                    status: null,
                },
            ],
        hasMultipleApprovers: t2cMultipleApproversEnabled,
    };
    const formMethods = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema),
        mode: 'onTouched',
    });
    const { control, handleSubmit, watch, formState: { errors: formErrors, isDirty }, } = formMethods;
    const watchT2cCheckbox = watch('t2cCheckbox');
    const shouldShowMultipleApprovers = watch('hasMultipleApprovers');
    const handleSuccess = (action) => {
        const text = getT2CSuccessMessage(action);
        onSuccess(action, text);
    };
    const handleFailure = (action, optionalMessage) => {
        onFailure(action, optionalMessage || ERR_GENERIC.message);
    };
    const unenroll = ({ additionalApprovers }) => {
        return unenrollPHAT2C({
            paymentId,
            t2cApprovers: shouldShowMultipleApprovers ? additionalApprovers : null,
        })
            .then((success) => {
            if (success) {
                return handleSuccess(TProHouseAccountAction.UNENROLL);
            }
            return handleFailure(TProHouseAccountAction.UNENROLL);
        })
            .catch(() => {
            return handleFailure(TProHouseAccountAction.UNENROLL);
        });
    };
    const enroll = ({ phoneNumber, additionalApprovers, }) => {
        return enrollPHAT2C({
            t2cNumber: removeNonDigitsFromString(phoneNumber),
            paymentId,
            locType: card === null || card === void 0 ? void 0 : card.locType,
            isResend: false,
            t2cApprovers: shouldShowMultipleApprovers ? additionalApprovers : null,
        })
            .then((success) => {
            if (success) {
                return handleSuccess(TProHouseAccountAction.ENROLL);
            }
            return handleFailure(TProHouseAccountAction.ENROLL);
        })
            .catch((error) => {
            var _a, _b;
            const errorCode = getPaymentsErrorCode(error);
            if (errorCode === MAX_T2C_TENDER_LIMIT_REACHED_CODE) {
                ptcRemotePurchasesPtcPostPageErrorAnalytics(ERR_MAX_T2C_TENDER_LIMIT.message);
                handleFailure(TProHouseAccountAction.ENROLL, ERR_MAX_T2C_TENDER_LIMIT.message);
                return;
            }
            if (errorCode === T2C_NUMBER_ASSOCIATED_ANOTHER_ACCOUNT) {
                const phoneNumbers = ((_b = (_a = error.errors) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.errorMessage)
                    ? extractPhoneNumbersFromErrorMessage(error.errors[0].errorMessage)
                    : [];
                const phoneNumbersText = formatRedactedPhoneNumbers(phoneNumbers);
                const errorMessage = getDuplicateT2cErrorMessage(phoneNumbers, phoneNumbersText);
                ptcRemotePurchasesPtcPostPageErrorAnalytics(errorMessage);
                setFormAlertMessage({
                    alertStatus: TAlertStatus.ERROR,
                    text: errorMessage,
                });
                return;
            }
            handleFailure(TProHouseAccountAction.ENROLL);
        });
    };
    const onSubmitForm = (values) => {
        setFormAlertMessage(null);
        if (!isDirty) {
            onCloseForm();
            return;
        }
        setIsSubmitting(true);
        // Did they uncheck the box?
        if (!values.t2cCheckbox) {
            const confirmModalProps = {
                bodyText: UnenrollT2CConfirmModalBodyText,
                headerText: UnenrollT2CConfirmModalHeaderText,
            };
            openConfirmModal(confirmModalProps)
                .then((confirmed) => {
                if (confirmed) {
                    return unenroll(values);
                }
                onCloseForm();
                return Promise.resolve();
            })
                .finally(() => {
                setIsSubmitting(false);
            });
            return;
        }
        enroll(values).finally(() => {
            setIsSubmitting(false);
        });
    };
    return (React.createElement("div", { className: "sm:sui-max-w-[485px] sui-flex sui-flex-col sui-gap-10" },
        React.createElement(FormTitle, { title: "Authorize pro trade credit for Remote Purchases", actionLabel: "Cancel", onActionClick: () => onCloseForm() }),
        React.createElement(Typography, null, "Let your team members check out at The Home Depot using Pro Trade Credit. Approve and verify in-store purchases via text message."),
        React.createElement(ProHouseAccountInfo, { proHouseAccount: card }),
        React.createElement("div", null,
            React.createElement(FormProvider, Object.assign({}, formMethods),
                React.createElement("form", { onSubmit: handleSubmit(onSubmitForm) },
                    React.createElement("div", { className: "sui-flex sui-gap-4" },
                        React.createElement("div", { className: "sui-mt-2 sui-w-6 sui-h-6" },
                            React.createElement(TextToConfirm, null)),
                        React.createElement("div", null,
                            React.createElement(SelectionControlLabel, { label: React.createElement("div", { className: "sui-flex sui-items-center" },
                                    React.createElement(Typography, { weight: "bold" }, "Text2Confirm")) },
                                React.createElement(Controller, { name: "t2cCheckbox", control: control, render: ({ field: { value, name, onChange, disabled, onBlur }, }) => (React.createElement(Checkbox, { name: name, onChange: onChange, disabled: disabled, onBlur: onBlur, checked: value, "data-testid": "t2c-checkbox", inputAttributes: { 'aria-label': 'controlled' } })) })),
                            React.createElement("div", { className: "sui-ml-[-16px] sui-inline-block" },
                                React.createElement(InfoPopover, { title: "Approve by Text2Confirm", iconSize: "small", onIconClick: () => onTooltipClick('T2C') },
                                    React.createElement("div", { className: "sui-w-[260px] sui-text-xs" }, "Receive a text message with checkout details and reply to approve the in-store purchase."))),
                            watchT2cCheckbox && (React.createElement(React.Fragment, null,
                                shouldShowMultipleApprovers ? (React.createElement("div", { className: "sui-mt-6", "data-testid": "pha-multi-approver-section" },
                                    React.createElement(MultipleApproversSection, null))) : (React.createElement(React.Fragment, null,
                                    React.createElement("div", { className: "sui-my-6" },
                                        React.createElement(Typography, null, "We\u2019ll send a text message to the phone number provided below.")),
                                    React.createElement(Controller, { name: "phoneNumber", control: control, render: (_a) => {
                                            var _b;
                                            var _c = _a.field, { ref, name, onChange, value } = _c, restFields = __rest(_c, ["ref", "name", "onChange", "value"]);
                                            return (React.createElement(TextField, { label: "Mobile Phone Number*", value: value, name: name, fullWidth: true, helpMessage: "This number is used for all cards authorized for Text2Confirm.", status: getTextFieldStatus(formErrors.phoneNumber), statusMessage: (_b = formErrors.phoneNumber) === null || _b === void 0 ? void 0 : _b.message, InputProps: Object.assign({ inputProps: { 'data-qm-mask': '' }, onChange: (event) => {
                                                        onChange(getHyphenatedPhoneNumber(event.currentTarget.value || ''));
                                                    } }, restFields) }));
                                        } }))),
                                React.createElement(T2CTermsAndConditions, { paymentType: "Pro Trade Credit" }))),
                            formAlertMessage && (React.createElement("div", { className: "sui-mt-10", "data-testid": "general-error-message" },
                                React.createElement(Alert, { status: formAlertMessage.alertStatus }, formAlertMessage.text))))),
                    React.createElement("div", { className: "sui-mt-10 sui-grid sui-grid-cols-1 sui-gap-5 sm:sui-grid-cols-2" },
                        React.createElement("div", { className: "sui-order-last sm:sui-order-first" },
                            React.createElement(Button, { fullWidth: true, variant: "secondary", onClick: onCloseForm }, "Cancel")),
                        React.createElement(LoadingButton, { loading: isSubmitting, fullWidth: true, variant: "primary", type: "submit" }, "Save")))))));
};
